import { useCallback, useState } from "react";
import { LayoutFunctions } from "../area/LayoutFunctions";
import { checkIsSelectedWallProp, SelectedItem } from "../SelectedItem";
import { isWallKey, RoomConfig } from "@/state/RoomConfig/RoomConfig";
import { Vector2Like } from "three";
import { WallPropConfig } from "@/state/RoomConfig/WallConfig";
import { round } from "lodash";
import { getWallPropAreaData, WallPropAreaData } from "./WallPropAreaData";

export const useWallPropDragging = (roomConfig: RoomConfig) => {
    const [dragConfig, setDragConfig] = useState<WallPropAreaData | undefined>(undefined);

    const handleDragStart = useCallback(
        (item: SelectedItem) => {
            if (!checkIsSelectedWallProp(item) || !item.parent || !isWallKey(item.parent)) {
                return;
            }

            const dragConfig = getWallPropAreaData(roomConfig, item.key, item.parent);
            if (!dragConfig) return;

            setDragConfig(dragConfig);
        },
        [roomConfig]
    );

    const handleDrag = useCallback(
        (cursor: Vector2Like): WallPropConfig | undefined => {
            if (!dragConfig) return;

            const coordinate = dragConfig.coordinateProvider(cursor);
            const freeSpace = LayoutFunctions["1axis"].getFreeSpaceForCoordinate(
                dragConfig.root,
                dragConfig.otherChildren,
                coordinate,
                dragConfig.propAreaConfig
            );

            if (!freeSpace || freeSpace.size < dragConfig.prop.width) {
                return undefined;
            }

            const desiredOffset = coordinate - dragConfig.prop.width / 2;
            const minOffset = freeSpace.start;
            const maxOffset = freeSpace.end - dragConfig.prop.width;

            const offset = round(Math.max(Math.min(desiredOffset, maxOffset), minOffset), 3);

            return {
                ...dragConfig.prop,
                offset,
            };
        },
        [dragConfig]
    );

    const handleDragEnd = useCallback(() => {
        setDragConfig(undefined);
    }, []);

    return {
        handleDragStart,
        handleDrag,
        handleDragEnd,
    };
};
